import {
  DropdownTableAutocompleteOption,
  DropdownTableAutocompleteOptionCampaign,
  DropdownTableAutocompleteOptionCommunityMember,
  DropdownTableAutocompleteOptionContentType,
  DropdownTableAutocompleteOptionDepartment,
  DropdownTableAutocompleteOptionExpert,
  DropdownTableAutocompleteOptionJournalist,
  DropdownTableAutocompleteOptionLinkedService,
  DropdownTableAutocompleteOptionOrganization,
  DropdownTableAutocompleteOptionOutlet,
  DropdownTableAutocompleteOptionTeamMember,
  DropdownTableAutocompleteOptionType,
  DropdownTableAutocompleteOptionUnit,
  DropdownTableContextValue,
  DropdownTableContextValueKey,
} from '../../shared/contexts/DropdownTableContext';
import dayjs from '../../utils/dayjs';
import { API, ServiceOrIssueStatus, TrackerDataType, Tone } from '../../shared/constants/constants';
import { TrackerFilterStatus } from './types';

type TrackerFilterTextQueryKey = 'service' | 'topic' | 'id';
type CurrentUserFilterIdentity = {
  id: string;
  name?: string;
};
type TeamMemberFilterSource = {
  _id: string;
  name: string;
  [key: string]: any;
};

export const getTrackerFilterTextInputQuery = (key: TrackerFilterTextQueryKey, text: string) => {
  if (!text) {
    return '';
  }
  return `${key}=${encodeURIComponent(text)}`;
};

const getStringListFilterQuery = (query: string, strList: string[]) => {
  if (strList.length === 0) {
    return '';
  }
  const statusNameListStr = strList.map((str) => `"${str}"`).join(', ');
  return `${query}=[${statusNameListStr}]`;
};

export const getTrackerFilterStatusFilterQuery = (statusNameList: string[]) => {
  return getStringListFilterQuery('status', statusNameList);
};

export const getTrackerFilterToneFilterQuery = (tonesList: string[]) => {
  return getStringListFilterQuery('tone', tonesList);
};

type TrackerFilterDateQueryKey = 'startDate' | 'endDate';

export const getTrackerFilterDateInputFilterQuery = (key: TrackerFilterDateQueryKey, date: Date | null) => {
  if (!date) {
    return '';
  }

  let outputDate = dayjs(date);
  if (key === 'endDate') {
    outputDate = outputDate.add(1, 'day');
  }

  return `${key}=${outputDate.toISOString()}`;
};

type TrackerFilterAutocompleteQueryKey =
  | 'lead/expert'
  | 'expert'
  | 'contenttype'
  | 'campaigns'
  | 'type'
  | 'types'
  | 'team members'
  | 'leads'
  | 'units'
  | 'outlet'
  | 'journalist'
  | 'linkedservice'
  | 'department'
  | 'organization'
  | 'communityMember'
  | 'audience';

export const getTrackerFilterAutocompleteFilterQuery = (
  key: TrackerFilterAutocompleteQueryKey,
  idOrNameList: string[]
) => {
  if (idOrNameList.length === 0) {
    return '';
  }

  const idOrNameListStr = idOrNameList.map((item) => `"${item}"`).join(', ');
  return `${key}=[${idOrNameListStr}]`;
};

export const getTrackerFilterComplexityFilter = (complexities: number[]) => {
  if (complexities.length === 0) {
    return '';
  }
  return `complexity=[${complexities.map((item) => item).join(', ')}]`;
};

export const getTrackerFilterQueryString = (filterQueries: string[]) => {
  if (filterQueries.length === 0) {
    return '';
  }

  return filterQueries.filter((item) => item !== '').join('&');
};

const getCurrentUserTeamMemberFilterValue = (
  teamMembers: TeamMemberFilterSource[] | undefined,
  currentUser: CurrentUserFilterIdentity
): DropdownTableAutocompleteOptionTeamMember[] => {
  if (!currentUser.id) {
    return [];
  }

  const teamMember = teamMembers?.find((lead) => lead._id === currentUser.id);
  const filterValue = teamMember ?? { _id: currentUser.id, name: currentUser.name || currentUser.id };

  return [
    {
      ...filterValue,
      firstLetter: filterValue.name.charAt(0),
    },
  ] as DropdownTableAutocompleteOptionTeamMember[];
};

const isOnlyInProgressStatusFilter = (statusFilter: TrackerFilterStatus[] | undefined) => {
  return (
    Array.isArray(statusFilter) &&
    statusFilter.length === 1 &&
    statusFilter[0]?.name === ServiceOrIssueStatus.IN_PROGRESS
  );
};

const isOnlyCurrentUserTeamMemberFilter = (
  teamMemberFilter: Array<{ _id?: string }> | undefined,
  currentUserId: string
) => {
  return (
    Boolean(currentUserId) &&
    Array.isArray(teamMemberFilter) &&
    teamMemberFilter.length === 1 &&
    teamMemberFilter[0]?._id === currentUserId
  );
};

export const getCurrentUserInProgressFilters = (
  filters: Record<string, any>,
  teamMembers: TeamMemberFilterSource[] | undefined,
  currentUser: CurrentUserFilterIdentity
): Record<string, any> => {
  const teamMemberFilterValue = getCurrentUserTeamMemberFilterValue(teamMembers, currentUser);
  const nextFilters: Record<string, any> = {
    ...filters,
    status: [{ name: ServiceOrIssueStatus.IN_PROGRESS }],
  };

  if (teamMemberFilterValue.length > 0) {
    nextFilters['Team Members'] = teamMemberFilterValue;
  } else {
    delete nextFilters['Team Members'];
  }

  return nextFilters;
};

export const isCurrentUserInProgressFilterActive = (filters: Record<string, any>, currentUserId: string) => {
  return (
    isOnlyInProgressStatusFilter(filters.status) &&
    isOnlyCurrentUserTeamMemberFilter(filters['Team Members'], currentUserId)
  );
};

export const removeCurrentUserInProgressFilters = (filters: Record<string, any>, currentUserId: string) => {
  const nextFilters = { ...filters };

  if (isOnlyInProgressStatusFilter(nextFilters.status)) {
    delete nextFilters.status;
  }

  if (isOnlyCurrentUserTeamMemberFilter(nextFilters['Team Members'], currentUserId)) {
    delete nextFilters['Team Members'];
  }

  return nextFilters;
};

export const getTrackerFilterAutoCompleteOptionsUseMemoFactoryFunc = (
  dropdownTableContext: DropdownTableContextValue,
  filterType: DropdownTableContextValueKey
): DropdownTableAutocompleteOption[] => {
  if (!dropdownTableContext) {
    return [];
  }

  switch (filterType) {
    case 'status': {
      return dropdownTableContext.status
        .filter((status) => !status.archived)
        .map((status) => ({
          ...status,
          firstLetter: status.name.charAt(0),
        }))
        .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter));
    }
    case 'expert': {
      return dropdownTableContext.expert
        .filter((expert) => !expert.archived)
        .map((expert) => ({
          ...expert,
          firstLetter: expert.name.charAt(0),
        }))
        .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter));
    }
    case 'campaigns': {
      return dropdownTableContext.campaigns
        .filter((campaign) => !campaign.archived)
        .map((campaign) => ({
          ...campaign,
          firstLetter: campaign.name.charAt(0),
        }))
        .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter));
    }
    case 'contentType': {
      return (dropdownTableContext.contentType || [])
        .filter((contentType) => !contentType.archived)
        .map((contentType) => ({
          ...contentType,
          firstLetter: contentType.name.charAt(0),
        }))
        .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter));
    }
    case 'type': {
      return dropdownTableContext.type
        .filter((type) => !type.archived)
        .map((type) => ({
          ...type,
          firstLetter: type.name.charAt(0),
        }))
        .sort((a, b) => -b?.type?.localeCompare(a?.type) || -b?.firstLetter?.localeCompare(a?.firstLetter));
    }
    case 'lead': {
      return dropdownTableContext.lead
        .map((teamMember) => ({
          ...teamMember,
          firstLetter: teamMember.name.charAt(0),
        }))
        .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter));
    }
    case 'units': {
      return dropdownTableContext.units
        .map((unit) => ({
          ...unit,
          firstLetter: unit.name.charAt(0),
        }))
        .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter));
    }
    case 'outlet': {
      return dropdownTableContext.outlet
        .map((outlet) => ({
          ...outlet,
          firstLetter: outlet.name.charAt(0),
        }))
        .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter));
    }
    case 'journalist': {
      return dropdownTableContext.journalist
        .map((journalist) => ({
          ...journalist,
          firstLetter: journalist.name.charAt(0),
        }))
        .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter));
    }
    case 'organization': {
      return dropdownTableContext.organization
        .map((organization) => ({
          ...organization,
          firstLetter: organization.name.charAt(0),
        }))
        .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter));
    }
    case 'communityMember': {
      return dropdownTableContext.communityMember
        .map((communityMember) => ({
          ...communityMember,
          firstLetter: communityMember.name.charAt(0),
        }))
        .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter));
    }
    case 'service': {
      return dropdownTableContext.service
        .map((service) => ({
          ...service,
          firstLetter: service.name.charAt(0),
        }))
        .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter));
    }
    case 'dept': {
      return dropdownTableContext.dept
        .map((dept) => ({
          ...dept,
          firstLetter: dept.name.charAt(0),
        }))
        .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter));
    }
    default: {
      // add additional logic if filter type not covered
      console.log(`${filterType} - this type of filter is not supported, generate autocomplete options failed`);
      return [];
    }
  }
};

export const getTrackerFilterAutoCompleteOptionsGroupByFunc = (filterType: DropdownTableContextValueKey) => {
  switch (filterType) {
    case 'expert': {
      return (option: DropdownTableAutocompleteOptionExpert) => option.firstLetter;
    }
    case 'campaigns': {
      return (option: DropdownTableAutocompleteOptionCampaign) => option.firstLetter;
    }
    case 'contentType': {
      return (option: DropdownTableAutocompleteOptionContentType) => option.firstLetter;
    }
    case 'type': {
      return (option: DropdownTableAutocompleteOptionType) => option.type;
    }
    case 'lead': {
      return (option: DropdownTableAutocompleteOptionTeamMember) => option.firstLetter;
    }
    case 'units': {
      return (option: DropdownTableAutocompleteOptionUnit) => option.firstLetter;
    }
    case 'outlet': {
      return (option: DropdownTableAutocompleteOptionOutlet) => option.firstLetter;
    }
    case 'journalist': {
      return (option: DropdownTableAutocompleteOptionJournalist) => option.firstLetter;
    }
    case 'organization': {
      return (option: DropdownTableAutocompleteOptionOrganization) => option.firstLetter;
    }
    case 'communityMember': {
      return (option: DropdownTableAutocompleteOptionCommunityMember) => option.firstLetter;
    }
    case 'service': {
      return (option: DropdownTableAutocompleteOptionLinkedService) => option.firstLetter;
    }
    case 'dept': {
      return (option: DropdownTableAutocompleteOptionDepartment) => option.firstLetter;
    }
    default: {
      // add additional logic if filter type not covered
      console.log(`${filterType} - this type of filter is not supported, generate groupBy function failed`);
      return (option: any) => option;
    }
  }
};

export const getTrackerFilterFromStoredFilter = (
  storedFilter: string,
  dropdownTable: DropdownTableContextValue,
  dataType: TrackerDataType
) => {
  try {
    const parsedFilter = JSON.parse(storedFilter);
    for (const filterType of Object.keys(parsedFilter)) {
      switch (filterType) {
        case 'startDate': {
          parsedFilter['Start Date'] = dayjs(parsedFilter[filterType]).toDate();
          delete parsedFilter[filterType];
          break;
        }
        case 'endDate': {
          parsedFilter['End Date'] = dayjs(parsedFilter[filterType]).subtract(1, 'day').toDate();
          delete parsedFilter[filterType];
          break;
        }
        case 'team members':
        case 'leads': {
          const filterArray = JSON.parse(parsedFilter[filterType]);
          parsedFilter['Team Members'] = filterArray
            .map((filter: string) => {
              return dropdownTable?.lead.find((lead) => lead._id === filter);
            })
            .filter(Boolean);
          delete parsedFilter[filterType];
          break;
        }
        case 'lead/expert':
        case 'expert': {
          const filterArray = JSON.parse(parsedFilter[filterType]);
          parsedFilter['Lead/Expert'] = filterArray
            .map((filter: string) => {
              return dropdownTable?.expert.find((expert) => expert._id === filter);
            })
            .filter(Boolean);
          delete parsedFilter[filterType];
          break;
        }
        case 'campaigns': {
          const filterArray = JSON.parse(parsedFilter[filterType]);
          parsedFilter['Campaigns'] = filterArray
            .map((filter: string) => {
              return dropdownTable?.campaigns.find((campaign) => campaign._id === filter);
            })
            .filter(Boolean);
          delete parsedFilter[filterType];
          break;
        }
        case 'contenttype': {
          const filterArray = JSON.parse(parsedFilter[filterType]);
          parsedFilter.contentType = filterArray
            .map((filter: string) => {
              return dropdownTable?.contentType?.find((contentType) => contentType._id === filter);
            })
            .filter(Boolean);
          delete parsedFilter[filterType];
          break;
        }
        case 'audience': {
          const filterArray = JSON.parse(parsedFilter[filterType]);
          parsedFilter['audience'] = filterArray
            .map((filter: string) => {
              // @ts-ignore audience exists in dropdown table response
              return dropdownTable?.audience?.find((audience) => audience._id === filter);
            })
            .filter(Boolean);
          break;
        }
        case 'linkedservice': {
          const filterArray = JSON.parse(parsedFilter[filterType]);
          parsedFilter['linkedService'] = filterArray
            .map((filter: string) => {
              return dropdownTable.service.find((service) => service._id === filter);
            })
            .filter(Boolean);
          delete parsedFilter[filterType];
          break;
        }
        case 'linkedinteraction': {
          const filterString = parsedFilter[filterType];
          parsedFilter['linkedInteraction'] = { _id: filterString };
          delete parsedFilter[filterType];
          break;
        }
        case 'linkedengagement': {
          const filterString = parsedFilter[filterType];
          parsedFilter['linkedEngagement'] = { _id: filterString };
          delete parsedFilter[filterType];
          break;
        }
        case 'linkedcontentlog': {
          const filterString = parsedFilter[filterType];
          parsedFilter['linkedContentLog'] = { _id: filterString };
          delete parsedFilter[filterType];
          break;
        }
        case 'tone': {
          parsedFilter[filterType] = JSON.parse(parsedFilter[filterType]);
          break;
        }
        case 'complexity': {
          const filterArray = JSON.parse(parsedFilter[filterType]);
          parsedFilter[filterType] = filterArray.map((filter: string) => ({ _id: filter }));
          break;
        }
        case 'communityMember':
        case 'organization':
        case 'journalist':
        case 'outlet':
        case 'type': {
          const filterArray = JSON.parse(parsedFilter[filterType]);
          parsedFilter[filterType] = filterArray
            .map((filter: string) => {
              // @ts-ignore
              return dropdownTable?.[filterType].find((type) => type._id === filter);
            })
            .filter(Boolean);
          break;
        }
        case 'types': {
          const filterArray = JSON.parse(parsedFilter[filterType]);
          parsedFilter.type = filterArray
            .map((filter: string) => {
              return dropdownTable?.type.find((type) => type._id === filter);
            })
            .filter(Boolean);
          delete parsedFilter[filterType];
          break;
        }
        case 'status':
        case 'units':
        case 'department': {
          const filterArray = JSON.parse(parsedFilter[filterType]);
          parsedFilter[filterType] = filterArray.map((filter: string) => ({ name: filter }));
          break;
        }
        case 'service':
        case 'topic':
          break;
        case 'month':
        case 'timezoneOffset':
          delete parsedFilter[filterType];
          break;
        default: {
          // add additional logic if filter type not covered
          console.log(`${filterType} - this type of filter is not supported, parsing function failed`);
          break;
        }
      }
    }
    return parsedFilter;
  } catch (e) {
    console.error(e);
    clearFilters(dataType);
    return {};
  }
};

export const getTrackerFilterQueryFromFilters = (filters: Record<string, any>, dataType: TrackerDataType): string => {
  if (!filters || Object.keys(filters).length === 0) {
    return '';
  }

  const statusNames = (filters.status as TrackerFilterStatus[] | undefined)?.map((s) => s.name) || [];
  const leadExperts = (filters['Lead/Expert'] as DropdownTableAutocompleteOptionExpert[] | undefined)?.map(
    (e) => e._id
  );
  const teamMembers = (filters['Team Members'] as DropdownTableAutocompleteOptionTeamMember[] | undefined)?.map(
    (t) => t._id
  );
  const campaigns = (filters.Campaigns as DropdownTableAutocompleteOptionCampaign[] | undefined)?.map((c) => c._id);
  const contentTypes = (filters.contentType as { _id: string }[] | undefined)?.map((c) => c._id);
  const types = (filters.type as DropdownTableAutocompleteOptionType[] | undefined)?.map((t) => t._id);
  const units = (filters.units as DropdownTableAutocompleteOptionUnit[] | undefined)?.map((u) =>
    encodeURIComponent(u.name)
  );
  const outlets = (filters.outlet as DropdownTableAutocompleteOptionOutlet[] | undefined)?.map((o) => o._id);
  const journalists = (filters.journalist as DropdownTableAutocompleteOptionJournalist[] | undefined)?.map(
    (j) => j._id
  );
  const organizations = (filters.organization as DropdownTableAutocompleteOptionOrganization[] | undefined)?.map(
    (o) => o._id
  );
  const communityMembers = (
    filters.communityMember as DropdownTableAutocompleteOptionCommunityMember[] | undefined
  )?.map((c) => c._id);
  const linkedServices = (filters.linkedService as DropdownTableAutocompleteOptionLinkedService[] | undefined)?.map(
    (ls) => ls._id
  );
  const complexities = (filters.complexity as { _id: number }[] | undefined)?.map((c) => c._id);
  const departments = (filters.department as DropdownTableAutocompleteOptionDepartment[] | undefined)?.map((d) =>
    encodeURIComponent(d.name)
  );
  const tones = (filters.tone as Tone[] | undefined) || [];
  const audiences = (filters.audience as { _id: string }[] | undefined)?.map((a) => a._id);

  const serviceOrTopicQueryValue = filters.serviceOrTopic || '';
  const topicQuery = getTrackerFilterTextInputQuery(
    'topic',
    filters.topic || (dataType === TrackerDataType.SERVICE ? '' : serviceOrTopicQueryValue)
  );
  const idQuery = getTrackerFilterTextInputQuery('id', filters.id || '');
  const statusQuery = getTrackerFilterStatusFilterQuery(statusNames);
  const startDateQuery = getTrackerFilterDateInputFilterQuery('startDate', filters['Start Date'] || null);
  const endDateQuery = getTrackerFilterDateInputFilterQuery('endDate', filters['End Date'] || null);
  const expertsQuery = getTrackerFilterAutocompleteFilterQuery(
    dataType === TrackerDataType.CONTENT_LOG ? 'expert' : 'lead/expert',
    leadExperts || []
  );
  const campaignQuery = getTrackerFilterAutocompleteFilterQuery('campaigns', campaigns || []);
  const contentTypeQuery = getTrackerFilterAutocompleteFilterQuery('contenttype', contentTypes || []);
  const typeQuery = getTrackerFilterAutocompleteFilterQuery(
    dataType === TrackerDataType.CONTENT_LOG ? 'types' : 'type',
    types || []
  );
  const teamMembersQuery = getTrackerFilterAutocompleteFilterQuery(
    dataType === TrackerDataType.CONTENT_LOG ? 'leads' : 'team members',
    teamMembers || []
  );
  const unitsQuery = getTrackerFilterAutocompleteFilterQuery('units', units || []);
  const outletsQuery = getTrackerFilterAutocompleteFilterQuery('outlet', outlets || []);
  const journalistsQuery = getTrackerFilterAutocompleteFilterQuery('journalist', journalists || []);
  const organizationsQuery = getTrackerFilterAutocompleteFilterQuery('organization', organizations || []);
  const communityMembersQuery = getTrackerFilterAutocompleteFilterQuery('communityMember', communityMembers || []);
  const linkedServiceQuery = getTrackerFilterAutocompleteFilterQuery('linkedservice', linkedServices || []);
  const linkedInteractionQuery = filters.linkedInteraction ? `linkedinteraction=${filters.linkedInteraction._id}` : '';
  const linkedEngagementQuery = filters.linkedEngagement ? `linkedengagement=${filters.linkedEngagement._id}` : '';
  const linkedContentLogQuery =
    dataType === TrackerDataType.SERVICE && filters.linkedContentLog
      ? `linkedcontentlog=${filters.linkedContentLog._id}`
      : '';
  const departmentQuery = getTrackerFilterAutocompleteFilterQuery('department', departments || []);
  const toneQuery = getTrackerFilterToneFilterQuery(tones);
  const audienceQuery = getTrackerFilterAutocompleteFilterQuery('audience', audiences || []);
  const serviceQuery = getTrackerFilterTextInputQuery(
    'service',
    filters.service || (dataType === TrackerDataType.SERVICE ? serviceOrTopicQueryValue : '')
  );
  const complexityQuery = getTrackerFilterComplexityFilter(complexities || []);

  const queries =
    dataType === TrackerDataType.CONTENT_LOG
      ? [
          topicQuery,
          idQuery,
          statusQuery,
          startDateQuery,
          endDateQuery,
          unitsQuery,
          audienceQuery,
          expertsQuery,
          contentTypeQuery,
          campaignQuery,
          typeQuery,
          linkedServiceQuery,
          teamMembersQuery,
          departmentQuery,
        ]
      : [
          serviceQuery,
          topicQuery,
          idQuery,
          statusQuery,
          startDateQuery,
          endDateQuery,
          expertsQuery,
          complexityQuery,
          campaignQuery,
          typeQuery,
          teamMembersQuery,
          unitsQuery,
          outletsQuery,
          journalistsQuery,
          organizationsQuery,
          communityMembersQuery,
          linkedServiceQuery,
          linkedInteractionQuery,
          linkedEngagementQuery,
          linkedContentLogQuery,
          departmentQuery,
          toneQuery,
        ];

  if (filters.month) {
    queries.push(`month=${filters.month}`);
  }
  if (filters.timezoneOffset !== undefined) {
    queries.push(`timezoneOffset=${filters.timezoneOffset}`);
  }

  return getTrackerFilterQueryString(queries);
};

export const clearFiltersRequest = async (dataType: TrackerDataType) => {
  let domain: string;
  switch (dataType) {
    case TrackerDataType.MEDIA_INTERACTION:
      domain = 'issues';
      break;
    case TrackerDataType.ENGAGEMENT:
      domain = 'engagements';
      break;
    case TrackerDataType.CONTENT_LOG:
      domain = 'content-logs';
      break;
    default:
      domain = 'services';
      break;
  }

  return API.put(`/${domain}/filter/clear`);
};

const clearFilters = async (dataType: TrackerDataType) => {
  await clearFiltersRequest(dataType).catch((err) => {
    console.error(err);
  });
};
